// ─── cq_tree_table_props.js ───────────────────────────────────────────────────
// Shared prop helpers for CQTreeTable, mirroring the existing my_audits.utils
// pattern so all three dashboard tables (Hospital, Department, Physician)
// can use identical boilerplate.

import React from 'react';
import AppColors from '../../../../constants/AppColors';
import { FONT_FAMILY } from '../../../../constants/Theme';
import {
  DownIcon,
  LeftIcon,
  RightIcon,
  SortIcon,
  UpIcon,
  LeftEnableIcon,
  RightEnableIcon,
} from '../../../../constants/IconData';
// ─── Columns whose header/cell content is left-aligned ───────────────────────
const LEFT_ALIGN_IDS = ['name', 'batch_id'];

export function getRowProps(row) {
  const bgs = ['white', '#f2faf2', '#f6f9fd', '#F5F3FF'];
  const bg  = bgs[Math.min(row.depth, bgs.length - 1)];
  return {
    style: { backgroundColor: bg }
  };
}

// ─── Default props shared by all three tree tables ───────────────────────────
export const defaultTreeTableProp = {
  // Sort icons
  renderSortIcon:     () => <SortIcon style={{ width: 15, height: 15, marginLeft: 1 }} />,
  renderSortUpIcon:   () => <UpIcon   style={{ width: 10, height: 10, marginLeft: 1 }} />,
  renderSortDownIcon: () => <DownIcon style={{ width: 10, height: 10, marginLeft: 1 }} />,

  // Navigation icons for main paginator
  LeftIcon:  <LeftIcon  style={{ cursor: 'pointer', height: 40, width: 40 }} />,
  RightIcon: <RightIcon style={{ cursor: 'pointer', height: 40, width: 40 }} />,
  getRowProps,
  // Cell styling
  getCellProps: (cell, data) => ({
    style: {
      fontFamily:  FONT_FAMILY.ptSans,
      color:       AppColors.secondary,
      fontWeight:  'bold',
      fontSize:    '14px',
      textAlign:   LEFT_ALIGN_IDS.includes(data?.column?.id) ? 'left' : 'center',
    },
  }),

  // Header cell styling
  getHeaderCellProps: (header) => ({
    textTransform: 'none',
    fontFamily:    FONT_FAMILY.ptSans,
    fontWeight:    'bold',
    color:         AppColors.black,
    justifyContent: LEFT_ALIGN_IDS.includes(header?.id) ? 'flex-start' : 'center',
  }),

  tableProps: { size: 'sm' },
};

// ─── createTreeTableProps ─────────────────────────────────────────────────────
// Enhanced version with highlighted row support.
// Pass the ID of the row that should be highlighted (e.g. currently selected).
export function createTreeTableProps(highlightedRowId) {
  return {
    ...defaultTreeTableProp,
    getRowProps: (row) => {
      if (!highlightedRowId || row.original?.id !== highlightedRowId) return {};
      return {
        style: {
          backgroundColor: `${AppColors.secondary}15`,
          borderLeft: `3px solid ${AppColors.secondary}`,
        },
      };
    },
  };
}

// ─── paginationProps ──────────────────────────────────────────────────────────
// Converts a usePagination hook object into CQTreeTable pagination props.
// Usage:  {...paginationProps(dashboardContext.getHospitals)}
export function paginationProps(pagination) {
  const canGoBack = pagination?.currentPage !== 1;
  const canGoNext = pagination?.totalPage !== pagination.currentPage;

  return {
    total: pagination.totalCount,
    pageCount: pagination.totalPage,
    size: pagination.showSize,
    currentPage: pagination.currentPage,
    LeftIcon: canGoBack ? <LeftEnableIcon /> : <LeftIcon />,
    RightIcon: canGoNext ? <RightEnableIcon /> : <RightIcon />,
  };
}